import { format, parse, isValid, isBefore, startOfDay } from 'date-fns';
import { sk } from 'date-fns/locale';

/**
 * Formátuje dátum do slovenského formátu
 * @param {Date|string} date - Dátum
 * @param {string} formatStr - Format string (default: 'dd.MM.yyyy')
 * @returns {string} Formátovaný dátum
 */
export const formatDate = (date, formatStr = 'dd.MM.yyyy') => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (!isValid(dateObj)) return '';
  
  return format(dateObj, formatStr, { locale: sk });
};

/**
 * Formátuje čas
 * @param {string} time - Čas v formáte HH:mm
 * @returns {string} Formátovaný čas
 */
export const formatTime = (time) => {
  if (!time) return '';
  return time.substring(0, 5); // HH:mm
};

/**
 * Formátuje dátum a čas spolu
 * @param {Date|string} date - Dátum
 * @param {string} time - Čas
 * @returns {string} Formátovaný dátum a čas
 */
export const formatDateTime = (date, time) => {
  return `${formatDate(date)} • ${formatTime(time)}`;
};

/**
 * Parsuje dátum zo stringu
 * @param {string} dateStr - Dátum string
 * @param {string} formatStr - Format string
 * @returns {Date|null} Parsovaný dátum alebo null
 */
export const parseDate = (dateStr, formatStr = 'yyyy-MM-dd') => {
  if (!dateStr) return null;
  
  const parsed = parse(dateStr, formatStr, new Date());
  return isValid(parsed) ? parsed : null;
};

/**
 * Konvertuje dátum do API formátu (YYYY-MM-DD)
 * @param {Date} date - Dátum
 * @returns {string} Dátum v API formáte
 */
export const toApiDate = (date) => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

/**
 * Konvertuje dátum z API formátu
 * @param {string} dateStr - Dátum string z API
 * @returns {Date|null} Date objekt
 */
export const fromApiDate = (dateStr) => {
  return parseDate(dateStr, 'yyyy-MM-dd');
};

/**
 * Zistí, či je dátum v minulosti
 * @param {Date|string} date - Dátum
 * @returns {boolean}
 */
export const isPastDate = (date) => {
  const dateObj = typeof date === 'string' ? fromApiDate(date) : date;
  if (!dateObj) return false;
  
  return isBefore(startOfDay(dateObj), startOfDay(new Date()));
};

/**
 * Získa dnešný dátum
 * @returns {Date}
 */
export const getToday = () => {
  return startOfDay(new Date());
};

/**
 * Získa dnešný dátum v API formáte
 * @returns {string}
 */
export const getTodayApi = () => {
  return toApiDate(new Date());
};

/**
 * Dni v týždni (slovensky)
 */
export const DAYS_OF_WEEK = [
  'Pondelok',
  'Utorok',
  'Streda',
  'Štvrtok',
  'Piatok',
  'Sobota',
  'Nedeľa'
];

/**
 * Mesiace (slovensky)
 */
export const MONTHS = [
  'Január',
  'Február',
  'Marec',
  'Apríl',
  'Máj',
  'Jún',
  'Júl',
  'August',
  'September',
  'Október',
  'November',
  'December'
];

/**
 * Získa názov dňa v týždni
 * @param {Date} date - Dátum
 * @returns {string}
 */
export const getDayName = (date) => {
  const dayIndex = date.getDay();
  return DAYS_OF_WEEK[dayIndex === 0 ? 6 : dayIndex - 1];
};

/**
 * Získa názov mesiaca
 * @param {Date} date - Dátum
 * @returns {string}
 */
export const getMonthName = (date) => {
  return MONTHS[date.getMonth()];
};
